// Turns a customer's payment record into the one word a user actually scans
// for: is this customer fine, drifting, or a problem?
//
// The thresholds are a judgement call, not law. They are here rather than in
// the report query so the wording and the cut-offs can be changed without
// touching SQL.

export type PaymentBehaviour = 'unknown' | 'onTime' | 'mostlyOnTime' | 'late' | 'inArrears';

// A customer with an unpaid invoice past its due date right now is in arrears,
// whatever their history says - that is the one that needs doing something
// about today.
export const IN_ARREARS_MIN_OVERDUE = 1;

// Below this share of late payments a customer still counts as reliable.
export const MOSTLY_ON_TIME_MAX_LATE_RATIO = 0.25;

export interface IPaymentBehaviourRow {
  paidCount: number;
  paidLateCount: number;
  overdueCount: number;
  remindersCount: number;
  interestChargesCount: number;
}

export const lateRatio = (row: IPaymentBehaviourRow): number | null => {
  if (row.paidCount === 0) return null;

  return row.paidLateCount / row.paidCount;
};

export const paymentBehaviour = (row: IPaymentBehaviourRow): PaymentBehaviour => {
  if (row.overdueCount >= IN_ARREARS_MIN_OVERDUE) return 'inArrears';

  const ratio = lateRatio(row);

  if (ratio === null) return 'unknown';
  if (ratio === 0) return 'onTime';
  if (ratio <= MOSTLY_ON_TIME_MAX_LATE_RATIO) return 'mostlyOnTime';

  return 'late';
};

// Whether anything has been done about this customer yet. A customer in
// arrears who has never been chased is the interesting case.
export const hasBeenChased = (row: IPaymentBehaviourRow): boolean => {
  return row.remindersCount > 0 || row.interestChargesCount > 0;
};
